/* global Trello */

// @ts-ignore
import VueScript2 from 'vue-script2'
import _ from 'lodash'
import DateTools from '@/DateTools'

/**
 * Una card di Trello
 */
export interface TrelloCard {
    id: string,
    name: string
    shortUrl: URL
    due: string
    board: TrelloBoard,
    labels: TrelloLabel[],
    desc?: string
}

/**
 * Una board di Trello
 */
export interface TrelloBoard {
    name: string
    id: string
    shortUrl: URL
}

/**
 * Un membro di Trello
 */
export interface TrelloMember {
    id: string
    fullName: string
    email?: string
    idBoards?: string[]
}

/**
 * Un'etichetta di una card di Trello
 */
export interface TrelloLabel {
    id: string
    idBoard: string
    name: string
    color: string | null
}

/**
 * Gruppo di card che scadono un dato giorno
 */
export interface TrelloCardsDueOnDate {
    due: string, // data di scadenza nel formato 2019-05-09T10:00:00.000Z
    cards: TrelloCard[] // lista di card Trello che scadono in quella data
}


export default class TrelloClient {

    /**
     * Proprietà contenente l'oggetto 'Trello' come definito nella
     * librearia client.js di Trello
     */
    client: any = {}

    /*
      _____           _   _
     |_   _|         (_) | |
       | |    _ __    _  | |_
       | |   | '_ \  | | | __|
      _| |_  | | | | | | | |_
     |_____| |_| |_| |_|  \__|
    */

    /**
     * Inizializza il client Trello.
     *
     * @return {Promise} Promessa nella quale puoi accedere
     * al client di Trello già autorizzato (this.client)
     */
    init(appKey: string) {
        return new Promise((resolve) => {
            this.loadLibrary(appKey).then(() => {
                // @ts-ignore
                this.client = Trello
                this.authorize().then(
                    () => { console.log('Autorizzato utente Trello'); resolve(this.client) },
                )
            })
        })
    }

    /**
     * Carica la librera client.js di Trello.
     *
     * Non esistendo un pacchetto npm del client.js di Trello,
     * andiamo a prenderci il JS usando il pacchetto vue-script2
     * e poi memorizziamo l'oggetto globale Trello dentro una
     * proprietà della classe. Questa porcata ovviamente
     * non è compatbile con SSR, ma a noi poco interessa.
     *
     * @return {Promise}
     */
    loadLibrary(appKey: string) {
        return new Promise((resolve, reject) => {
            VueScript2.load('https://code.jquery.com/jquery-3.4.1.min.js').then(() => {
                console.log('Caricato jQuery')
                VueScript2.load('https://trello.com/1/client.js?key=' + appKey).then(
                    () => { console.log('Caricato client.js di Trello'); resolve() },
                    () => { reject(new Error('Non ho potuto caricare client.js di Trello')) },
                )
            }).catch(() => {
                new Error('Non ho potuto caricare jQuery')
            })
        })
    }

    /**
     * Chiedi all'utente di autorizzarci ad usare un suo
     * account Trello
     *
     * @return {Promise}
     */
    authorize() {
        return new Promise((resolve, reject) => {
            this.client.authorize({
                type: 'redirect',
                name: 'Trello Create Lists',
                scope: {
                    read: true,
                    write: true,
                },
                expiration: 'never',
                success: resolve(this.client),
                error() { reject(new Error('Failed authentication with Trello')) },
            })
        })
    }

    /*
      ______          _            _
     |  ____|        | |          | |
     | |__      ___  | |_    ___  | |__
     |  __|    / _ \ | __|  / __| | '_ \
     | |      |  __/ | |_  | (__  | | | |
     |_|       \___|  \__|  \___| |_| |_|
    */

    /**
     * Get the authenticated Trello member
     * https://developers.trello.com/reference/#member-object
     */
    fetchMyself() {
        return new Promise((resolve, reject) => {
            this.client.get(
                '/members/me?fields=id,fullName,email',
                (member: TrelloMember) => resolve(member),
                () => { reject(new Error('Failed to load board members')) },
            )
        })
    }

    /**
     * Get board members
     * https://developers.trello.com/reference/#member-object
     */
    fetchBoardMembers(boardId: string) {
        return new Promise((resolve, reject) => {
            this.client.get(
                '/boards/' + boardId + '/members?fields=fullName,avatarUrl',
                (members: TrelloMember[]) => resolve(members),
                () => { reject(new Error('Failed to load board members')) },
            )
        })
    }

    /**
     * Fetch a card from Trello given its ID.
     * Ref: https://developers.trello.com/reference/#cardsid
     *
     * @param cardId
     * @param cardFields Card fields to include in returned card, default is all
     * @param boardFields Board fields to include in returned card, default is none
     */
    fetchCard(cardId: string, cardFields = ['all'], boardFields: string[] = []) {
        return new Promise((resolve, reject) => {

            let queryOptions = [
                'card_fields=' + cardFields.join(','),
                'board_fields=' + boardFields.join(','),
                'board=' + (boardFields.length > 0 ? 'true' : 'false'),
            ]

            this.client.get(
                '/cards/' + cardId + '?' + queryOptions.join('&'),
                (card: TrelloCard) => resolve(card),
                () => { reject(new Error('Failed to fetch card ' + cardId)) },
            )
        })
    }

    /**
     * Get open cards of given member using the search
     * endpoint.
     *
     * A little ambiguity: open in this context means that
     * the card has a due date and that due date is not
     * completed, contrary to the usual Trello meaning of open
     * as non-closed, that is, non archived.
     *
     * Optionally specify argument boardPattern to restrict
     * the search to boards containing that substring or ID
     *
     * The output cards will contain also board information
     * in the 'board' property (id, name, shortUrl).
     *
     * Reference for the search endpoing:
     * https://developers.trello.com/reference/#search
     */
    fetchMemberOpenCards(memberId: string, cardFields: string[], cardsLimit = 200, boardPattern = 'all') {
        return new Promise((resolve, reject) => {

            let searchQuery = [
                'member:' + memberId,
                'due:incomplete',
                'sort:due',
                'is:open',
            ]

            if (boardPattern !== 'all') {
                searchQuery.push('board:"' + boardPattern + '"')
            }

            // Options for the search
            let queryOptions = [
                'card_fields=' + cardFields.join(','),
                'cards_limit=' + cardsLimit,
                'card_board=true',
                'board_fields=name,shortUrl',
            ]

            this.client.get(
                '/search?query=' + searchQuery.join('%20') + '&' + queryOptions.join('&'),
                // @ts-ignore
                (res: object[]) => resolve(res.cards),
                () => { reject(new Error('Failed to fetch cards of member')) },
            )
        })
    }

    /**
     * Same as fetchMemberOpenCards(), but you can specify multiple
     * board patterns.
     *
     * The result will be the list of unique cards in all boards
     * whose name or ID matches at least one of the given board
     * patterns.
     *
     * Any sorting specified via the 'sort' parameter in Trello's
     * search query will be lost, so make sure to re-sort the returned
     * array according to your needs.
     *
     * Trello will be interrogated once for each of the give board
     * patterns.
     */
    fetchMemberOpenCardsAcrossBoards(memberId: string, cardFields: string[], cardsLimit = 200, boardPatterns = ['all']) {
        return new Promise((resolve, reject) => {
            let promises = boardPatterns.map(pattern => this.fetchMemberOpenCards(memberId, cardFields, cardsLimit, pattern))
            Promise.all(promises).then((cardsArray) => {
                resolve(TrelloClient.sortByDate(cardsArray.flat()))
            })
        })
    }

    /**
     * Return labels of given board.
     *
     * Label object: https://developers.trello.com/reference#label-object
     *
     * @param boardId
     */
    fetchBoardLabels(boardId: string) {
        return new Promise((resolve, reject) => {
            this.client.get(
                '/boards/' + boardId + '/labels/',
                (labels: TrelloLabel[]) => resolve(labels),
                () => { reject(new Error('Failed to fetch labels of board ' + boardId)) },
            )
        })
    }

    /*
       _____   _
      / ____| | |
     | |      | |__     __ _   _ __     __ _    ___
     | |      | '_ \   / _` | | '_ \   / _` |  / _ \
     | |____  | | | | | (_| | | | | | | (_| | |  __/
      \_____| |_| |_|  \__,_| |_| |_|  \__, |  \___|
                                        __/ |
                                       |___/
    */

    /**
     * Complete card (check due date)
     */
    completeCard(cardId: string) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?dueComplete=true',
                () => resolve(),
                () => { reject(new Error('Failed to complete card ' + cardId)) },
            )
        })
    }

    /**
     * Un-complete card (uncheck due date)
     */
    uncompleteCard(cardId: string) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?dueComplete=false',
                () => resolve(),
                () => { reject(new Error('Failed to un-complete card ' + cardId)) },
            )
        })
    }

    /**
     * Un-complete and un-archive card
     */
    reviveCard(cardId: string) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?dueComplete=false&closed=false',
                () => resolve(),
                () => { reject(new Error('Failed to revive card ' + cardId)) },
            )
        })
    }

    /**
     * Archive card
     */
    archiveCard(cardId: string) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?closed=true',
                () => resolve(),
                () => { reject(new Error('Failed to archive card ' + cardId)) },
            )
        })
    }

    /**
     * Unarchive card
     */
    unarchiveCard(cardId: string) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?closed=false',
                () => resolve(),
                () => { reject(new Error('Failed to unarchive card ' + cardId)) },
            )
        })
    }

    /**
     * Update due date
     */
    updateDueDate(cardId: string, date: Date) {
        return new Promise((resolve, reject) => {
            this.client.put(
                '/cards/' + cardId + '/?due=' + date.toISOString(),
                () => resolve(),
                () => { reject(new Error('Failed to set due date ' + date.toISOString() + ' on card ' + cardId)) },
            )
        })
    }

    /**
     * Comment on a card
     */
    commentOnCard(cardId: string, text: string) {
        return new Promise((resolve, reject) => {
            this.client.post(
                '/cards/' + cardId + '/actions/comments?id=' + cardId + '&text=' + text,
                () => resolve(),
                () => { reject(new Error('Failed to write comment on card ' + cardId)) },
            )
        })
    }

    /*
      _    _   _     _   _
     | |  | | | |   (_) | |
     | |  | | | |_   _  | |  ___
     | |  | | | __| | | | | / __|
     | |__| | | |_  | | | | \__ \
      \____/   \__| |_| |_| |___/
    */

    /**
     * Given an array of Trello cards, return an array with
     * statistics about overdue cards.
     *
     * @param {TrelloCard[]} cards
     * @return {Object[]} Statistics computed from the input cards,
     * namely:
     *  - {int} nTasks: Number of tasks
     *  - {int} nOverdueTasks: Number of overdue tasks
     *  - {int} nOverdueTasks7: Number of tasks overdue 7 days or more
     *  - {float} nOverdueTasks7Percent: Percent of tasks overdye 7 days or more
     */
    static cardsStatistics(cards: TrelloCard[]): Object {

        // Remaining days before the due date
        let remainingDays = _.map(cards, card => DateTools.daysFromToday(card.due))

        let nTasks = cards.length
        let nOverdueTasks = _.filter(remainingDays, days => days < 0).length
        let nOverdueTasksPercent = (nOverdueTasks / nTasks * 100).toFixed(2)
        let nOverdueTasks7 = _.filter(remainingDays, days => days <= -7).length
        let nOverdueTasks7Percent = (nOverdueTasks7 / nTasks * 100).toFixed(2)

        return {
            nTasks,
            nOverdueTasks,
            nOverdueTasksPercent,
            nOverdueTasks7,
            nOverdueTasks7Percent,
        }
    }

    /**
     * Return the given cards sorted by date ascending and de-duplicated
     *
     * @param cards
     */
    static sortByDate(cards: TrelloCard[]): TrelloCard[] {
        return cards.sort((card1, card2) => new Date(card1.due).getTime() - new Date(card2.due).getTime())
    }

}
